import dayjs from "dayjs";

// URL filters that the Executive Dashboard hands to the pages its numbers open
// (Phase 2 section 8). Each reader validates at the boundary, so a page never
// forwards a malformed or unknown value to the backend: it is dropped instead.
// The dashboard sends `from=dashboard`, which switches the chip label to
// "Filtered from Dashboard".

const DATE_FORMAT = "YYYY-MM-DD";

const parseId = (raw) => {
  if (raw === null || raw === undefined) return null;
  const str = String(raw).trim();
  if (!/^\d+$/.test(str)) return null;
  const parsed = Number(str);
  return Number.isSafeInteger(parsed) && parsed > 0 ? String(parsed) : null;
};

// Both dates or neither; a strict, non-inverted pair.
const parseDateRange = (params, startKey, endKey) => {
  const start = params.get(startKey);
  const end = params.get(endKey);
  if (!start || !end) return { start: null, end: null };
  const a = dayjs(start, DATE_FORMAT, true);
  const b = dayjs(end, DATE_FORMAT, true);
  if (!a.isValid() || !b.isValid() || b.isBefore(a, "day")) {
    return { start: null, end: null };
  }
  return { start, end };
};

const parseFrom = (params) => (params.get("from") === "dashboard" ? "dashboard" : null);

export const filterChipLabel = (from) =>
  from === "dashboard" ? "Filtered from Dashboard" : "Filtered";

export const filterChipClearLabel = (from) =>
  from === "dashboard" ? "Clear Dashboard filter" : "Clear imported filter";

export const formatDateRangeLabel = (start, end) =>
  `${dayjs(start, DATE_FORMAT).format("MMM D, YYYY")} - ${dayjs(end, DATE_FORMAT).format("MMM D, YYYY")}`;

// ---------------------------------------------------------------- Accounts

export const ACCOUNT_ACTIVITY_LABELS = {
  dormant_30_59: "No activity for 30-59 days",
  dormant_60_89: "No activity for 60-89 days",
  dormant_90_plus: "No activity for 90+ days",
  never: "Never ordered",
};

export const PAST_DUE_THRESHOLD_LABELS = {
  warning: "Over past-due warning",
  limit: "Over past-due limit",
};

export const ACCOUNTS_LINK_KEYS = [
  "activity",
  "pastDueThreshold",
  "activeFrom",
  "activeTo",
  "regionId",
  "from",
];

export const readAccountsLinkFilters = (search) => {
  const params = new URLSearchParams(search);
  const activity = params.get("activity");
  const threshold = params.get("pastDueThreshold");
  const active = parseDateRange(params, "activeFrom", "activeTo");
  return {
    activity: activity && ACCOUNT_ACTIVITY_LABELS[activity] ? activity : null,
    pastDueThreshold: threshold && PAST_DUE_THRESHOLD_LABELS[threshold] ? threshold : null,
    activeFrom: active.start,
    activeTo: active.end,
    regionId: parseId(params.get("regionId")),
    from: parseFrom(params),
  };
};

// ------------------------------------------------------------------ Orders

export const ORDERS_LINK_KEYS = ["status", "uninvoiced", "startDate", "endDate", "regionId", "from"];

// `validStatuses` are the status values the page's own filter buttons offer.
export const readOrdersLinkFilters = (search, validStatuses = []) => {
  const params = new URLSearchParams(search);
  const status = params.get("status");
  const dates = parseDateRange(params, "startDate", "endDate");
  return {
    status: status && validStatuses.includes(status) ? status : null,
    uninvoiced: params.get("uninvoiced") === "1",
    startDate: dates.start,
    endDate: dates.end,
    regionId: parseId(params.get("regionId")),
    from: parseFrom(params),
  };
};

// ---------------------------------------------------------------- Payments

export const PAYMENTS_LINK_KEYS = ["startDate", "endDate", "regionId", "population", "from"];

export const readPaymentsLinkFilters = (search) => {
  const params = new URLSearchParams(search);
  const dates = parseDateRange(params, "startDate", "endDate");
  return {
    startDate: dates.start,
    endDate: dates.end,
    regionId: parseId(params.get("regionId")),
    population: params.get("population") === "collected" ? "collected" : null,
    from: parseFrom(params),
  };
};

// --------------------------------------------------------------- Approvals

// A tab from the URL, only when the page offers it.
export const readApprovalsTab = (search, allowedTabs = []) => {
  const tab = new URLSearchParams(search).get("tab");
  return tab && allowedTabs.includes(tab) ? tab : null;
};

// Removes the given keys from a query string, leaving every other key alone.
export const withoutKeys = (search, keys) => {
  const params = new URLSearchParams(search);
  keys.forEach((key) => params.delete(key));
  return params.toString();
};
